import { Box } from '@mui/material';
import cn from 'classnames';
import { MuiTelInput, matchIsValidTel } from 'mui-tel-input';
import React, { useMemo } from 'react';
import { Control, Controller, FieldValues, Path } from 'react-hook-form';

import { ERROR_CLASSNAME } from '../../constants';
import { Label } from '../FormItem/Label';
import { Value } from '../FormItem/Value';
import styles from './PhoneNumberInput.module.css';
import { useAuth } from '../../../../../../api/auth';

interface PhoneNumberInputProps<T extends FieldValues> {
    name: Path<T>;
    label: string;
    control: Control<T, any>;
    errorMessage?: string;
    editMode: boolean;
}

export const PhoneNumberInput = <T extends FieldValues>({
    name,
    label,
    editMode,
    control,
    errorMessage,
}: PhoneNumberInputProps<T>) => {
    const { user } = useAuth();

    const defaultCountry = useMemo(() => {
        switch (user?.organization?.country?.id) {
            case 'MWI':
                return 'MW';
            case 'ZWE':
                return 'ZW';
            case 'TZA':
                return 'TZ';
            case 'ISL':
                return 'IS';
            default:
                return 'US';
        }
    }, [user]);

    return (
        <Box display="flex" flexDirection="column" className={cn(styles.container, errorMessage && ERROR_CLASSNAME)}>
            <Label type={errorMessage ? 'error' : 'default'} content={label} />
            <Controller
                name={name}
                control={control}
                rules={{ validate: (value) => matchIsValidTel(value ?? '') }}
                render={({ field: { ref: fieldRef, value, ...fieldProps } }) => (
                    <>
                        {!editMode ? (
                            <Value content={value || 'N/A'} />
                        ) : (
                            <MuiTelInput
                                {...fieldProps}
                                className={styles.phoneNumberInput}
                                value={value ?? ''}
                                inputRef={fieldRef}
                                defaultCountry={defaultCountry}
                                helperText={errorMessage}
                                error={!!errorMessage}
                                inputProps={{
                                    maxLength: 20,
                                }}
                            />
                        )}
                    </>
                )}
            />
        </Box>
    );
};
